import { forwardRef, useImperativeHandle, useState } from "react";
import { i18n, TravelPurposeEnum, TravelPurposeEnumLabel, TravelSportActivitiesEnum, TravelSportActivitiesEnumLabel, useForm, useGetSportActivities } from "src/common";
import { DontSellModal, InputNumWithIcons, InputText, RadioButtonGroup, Select, ToggleInput } from "src/components";
import { FieldConfig, Form, FormRef } from "./form";

export type PassengerDataFormItemType = 'numOfInsuredPersons' | 'numOfInsuredChildren' | 'numOfInsuredAdults' | 'allInsuredPersonsAreFromTheSameFamily' | 'insuranceRange' | 'travelPurpose' | 'willWorkOnTrip' | 'email' | 'sportActivityCode' | 'sportActivities' | 'hasInsuredChildren';
export const allPassengerDataFormItems: Array<PassengerDataFormItemType> = ['numOfInsuredPersons', 'numOfInsuredChildren', 'numOfInsuredAdults', 'allInsuredPersonsAreFromTheSameFamily', 'insuranceRange', 'travelPurpose', 'willWorkOnTrip', 'email', 'sportActivityCode', 'sportActivities', 'hasInsuredChildren'];

export interface PassengerDataFormValues {
  numOfInsuredPersons: number;
  numOfInsuredChildren: number;
  numOfInsuredAdults: number;
  allInsuredPersonsAreFromTheSameFamily: boolean;
  travelPurpose: TravelPurposeEnum;
  sportActivities: TravelSportActivitiesEnum;
  sportActivityCode?: string;
  willWorkOnTrip: string;
  email: string;
  luggageValue?: number;
  tripValue?: number;
  hasInsuredChildren?: boolean;
}

interface PassengerDataFormProps {
  passengerData?: PassengerDataFormValues;
  formItems?: Array<PassengerDataFormItemType>;
  onSubmit?: (values: PassengerDataFormValues) => void;
}

export const PassengerDataForm = forwardRef<FormRef, PassengerDataFormProps>(({ passengerData, formItems = allPassengerDataFormItems, onSubmit }, ref) => {
  const { formRef, submitForm, getValues } = useForm();
  const { sportActivities } = useGetSportActivities();
  const [dontSellModalOpen, setDontSellModalOpen] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    submitForm: () => { submitForm(); },
    getValues: () => { return getValues(); }
  }));

  const handleSubmit = (values: PassengerDataFormValues) => {
    if (onSubmit) onSubmit(values);
  };

  const fields: FieldConfig<PassengerDataFormValues>[] = [
    {
      name: 'numOfInsuredPersons',
      label: i18n.t('components.forms.passengerData.numOfInsuredPersons'),
      component: InputNumWithIcons,
      componentProps: {
        min: 1,
        max: 20,
        step: 1,
       // description: i18n.t('components.forms.passengerData.numOfInsuredPersonsDescription')
      },
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.passengerData.numOfInsuredPersonsRequired')
        }
      ]
    },
    {
      name: 'allInsuredPersonsAreFromTheSameFamily',
      label: i18n.t('components.forms.passengerData.allInsuredPersonsAreFromTheSameFamily'),
      component: ToggleInput,
      componentProps: {
        description: i18n.t('components.forms.passengerData.allInsuredPersonsAreFromTheSameFamilyDescription')
      },
      showIf: (values: PassengerDataFormValues) => values.numOfInsuredPersons > 1
    },
    {
      name: 'hasInsuredChildren',
      label: i18n.t('components.forms.passengerData.hasInsuredChildren'),
      component: ToggleInput,
      onChange: (value: boolean) => {
        formRef?.current?.setFieldValue?.('hasInsuredChildren', value);
        if (!value) {
          formRef?.current?.setFieldValue?.('numOfInsuredChildren', 0);
        } else {
          formRef?.current?.setFieldValue?.('numOfInsuredChildren', 1);
        }
      }
    },
    {
      name: 'numOfInsuredChildren',
      label: i18n.t('components.forms.passengerData.numOfInsuredChildren'),
      component: InputNumWithIcons,
      componentProps: {
        min: 1,
        max: 20,
        step: 1
      //  description: i18n.t('components.forms.passengerData.numOfInsuredChildrenDescription')
      },
      validations: [
        {
          type: 'notLargerThan',
          message: i18n.t('components.forms.passengerData.numOfInsuredChildrenNotLargerThan'),
          params: { maxValue: 'numOfInsuredPersons' }
        }
      ],
      showIf: (values: PassengerDataFormValues) => values.hasInsuredChildren ?? false
    },
    {
      name: 'travelPurpose',
      label: i18n.t('components.forms.passengerData.travelPurpose'),
      component: RadioButtonGroup,
      componentProps: {
        horizontal: true
      },
      // Poslovno is last so arrow keys can move from Turističko to Sportsko without opening the modal
      options: [TravelPurposeEnum.TURISTIC, TravelPurposeEnum.SPORT, TravelPurposeEnum.WORK].map((value) => ({ label: TravelPurposeEnumLabel[value], value })),
      onChange: (value: any) => {
        if (value === 'work') {
          setDontSellModalOpen(true);
          // Use setTimeout to ensure this runs after the current render cycle
          setTimeout(() => {
            formRef?.current?.setFieldValue?.('travelPurpose', 'turistic');
          }, 0);
        }
      }
    },
    {
      name: 'sportActivities',
      label: i18n.t('components.forms.passengerData.sportActivities'),
      component: RadioButtonGroup,
      componentProps: {
        horizontal: true
      },
      options: Object.values(TravelSportActivitiesEnum)
        .filter((value) => value !== TravelSportActivitiesEnum.NONE)
        .map((value) => ({ label: TravelSportActivitiesEnumLabel[value], value })),
      showIf: (values: PassengerDataFormValues) => values.travelPurpose === TravelPurposeEnum.SPORT
    },
    {
      name: 'sportActivityCode',
      label: i18n.t('components.forms.passengerData.sportActivityCode'),
      component: Select,
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.passengerData.sportActivityCodeRequired')
        }
      ],
      componentProps: {
        options: sportActivities?.map((sportActivity) => ({ label: sportActivity.label, value: sportActivity.value })) || [],
        placeholder: i18n.t('components.forms.passengerData.sportActivityCodePlaceholder')
      },
      showIf: (values: PassengerDataFormValues) => values.travelPurpose === TravelPurposeEnum.SPORT
    },
    {
      name: 'email',
      label: i18n.t('components.forms.passengerData.email'),
      component: InputText,
      componentProps: {
        type: 'email',
        maxLength: 50
      },
      validateOnBlur: true,
      validations: [
        { type: 'required', message: i18n.t('components.forms.passengerData.emailRequired') },
        { type: 'validMail', message: i18n.t('components.forms.passengerData.emailInvalid') },
        { type: 'noDiacriticsBeforeAt', message: i18n.t('components.forms.passengerData.emailDiacritics') },
      ]
    }
  ].filter((field) => formItems.includes(field.name as PassengerDataFormItemType));


  const initialValues = { ...passengerData, hasInsuredChildren: passengerData?.numOfInsuredChildren ? passengerData.numOfInsuredChildren > 0 : false };

  return (
    <>
      <Form ref={formRef} initialValues={initialValues} fields={fields} onSubmit={handleSubmit} />
      <DontSellModal open={dontSellModalOpen} onClose={() => setDontSellModalOpen(false)} />
    </>
  )
});
